import { useIntl } from '@/intl'

import { mapOptions } from '@/scopes/common/components/Filters/GroupedAirportFilter/mapper'
import { OptionGroup } from '@/scopes/common/components/Filters/GroupedAirportFilter/OptionGroup'

import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { PricesFilter } from '@/scopes/prices/components/Filters/BaseFilter'
import SelectedValue from '@/scopes/prices/components/Filters/BaseFilter/MultiSelectPricesFilter/SelectedValue'
import { PricesUseCasesContext } from '@/scopes/prices/components/PricesUseCasesContext'
import {
  departureAirportSelector,
  getFiltersSelector,
  getPricesSelector,
  transportTypeFilterOptionsSelector,
} from '@/scopes/prices/components/selectors'
import { createPricesFilterClickEvent } from '@/scopes/prices/domain/analytics'
import { FiltersInPricesValues } from '@/scopes/prices/domain/filters/types'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import * as DialogPrimitive from '@/scopes/search/components/QuickSearchBarLegacy/DialogPrimitive'
import {
  Button,
  ButtonLoaderIcon,
  DropdownMultiselect,
  FormField,
  FormFieldControl,
  FormFieldLabel,
  Typography,
} from '@sunwebgroup/ui-components'
import { isEmpty } from 'ramda'
import * as React from 'react'

export function GroupedAirportFilter() {
  const intl = useIntl()
  const { selectFilter } = React.useContext(PricesUseCasesContext)
  const setSelection = (selection: FiltersInPricesValues) => selectFilter({ type: 'DepartureAirport', selection })
  const { selectedValue: transportType } = usePricesSelector(transportTypeFilterOptionsSelector)
  const { options, selectedValues } = usePricesSelector(departureAirportSelector)
  const { isEmpty: isEmptyResponse } = usePricesSelector(getFiltersSelector)
  const { loading: isFetching } = usePricesSelector(getPricesSelector)
  const { sendEvent } = useAnalytics()
  const disabled = isFetching || isEmptyResponse
  const [open, setOpen] = React.useState<boolean>(false)
  const { isTablet } = useBreakpoints()
  const [localSelectedValues, setLocalSelectedValues] = React.useState<string[]>(selectedValues)
  const mappedOptions = React.useMemo(() => mapOptions(options), [options])

  function onSelectedValueChange(selection: string[]) {
    setSelection(selection)
    sendEvent(
      createPricesFilterClickEvent({
        element: 'dropdown',
        type: 'airports',
        result: selection.join(','),
      }),
    )
  }

  const mapChips = React.useCallback(
    (selectedFilterIds: string[]): string[] => {
      const fullySelectedGroups = mappedOptions
        .map((group) => {
          const atLeastOneChildNotSelected = group.children?.some(child => !selectedFilterIds.includes(child.id))

          return atLeastOneChildNotSelected ? undefined : group.value
        })
        .filter(group => group !== undefined) as string[]

      const notFullySelectedGroupOptions = mappedOptions
        .filter(group => !fullySelectedGroups.includes(group.value))
        .flatMap(group => group.children)
        .reduce<string[]>((acc, option) => {
          if (option && selectedFilterIds.includes(option.id)) {
            acc.push(option.value)
          }
          return acc
        }, [])

      return [...fullySelectedGroups, ...notFullySelectedGroupOptions]
    },
    [options],
  )
  const [chips, setChips] = React.useState<string[]>(mapChips(localSelectedValues))

  React.useEffect(() => {
    setLocalSelectedValues(selectedValues)
    setChips(mapChips(selectedValues))
  }, [selectedValues])

  const getChipId = React.useCallback(
    (value: string): string[] => {
      const parentGroup = mappedOptions.find(group => group.value === value)
      if (parentGroup) {
        return parentGroup.children?.map(child => child.id) || []
      }
      const option = mappedOptions.flatMap(group => group.children).find(child => child?.value === value)
      return option?.id ? [option.id] : []
    },
    [options],
  )

  function confirmSelection(selection: string[]) {
    onSelectedValueChange(selection)
  }

  const handleOnChange = (newValue: string[]) => {
    setLocalSelectedValues(newValue)
    setChips(mapChips(newValue))
    if (!open) {
      confirmSelection(newValue)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (disabled)
      return
    setOpen(newOpen)
    if (!newOpen) {
      confirmSelection(localSelectedValues)
    }
  }

  const onRemoveChip = (value: string) => {
    const id = getChipId(value)
    const newValue = localSelectedValues.filter(val => !id.includes(val))
    setLocalSelectedValues(newValue)
    setChips(mapChips(newValue))
    if (!open) {
      confirmSelection(newValue)
    }
  }

  function toggle() {
    if (disabled)
      return
    if (!open) {
      setOpen(true)
    }
    else {
      handleOpenChange(false)
      setOpen(false)
    }
  }

  const doNotRender
    = (isEmpty(options) && !isEmptyResponse)
      || transportType !== 'Flight'
      || (!isTablet && !isEmptyResponse && options.length <= 1)

  if (doNotRender) {
    return null
  }

  return (
    <FormField as="div">
      <FormFieldLabel>{intl.formatMessage({ id: 'prices_filters_departureairport_label' })}</FormFieldLabel>
      <FormFieldControl>
        <DropdownMultiselect.Root
          value={localSelectedValues}
          onValueChange={handleOnChange}
          onOpenChange={handleOpenChange}
          placeholder={intl.formatMessage({ id: 'prices_filters_departureairport_placeholder' })}
          asDialog={!isTablet}
          open={disabled ? false : open}
          disabled={disabled}
        >
          <DropdownMultiselect.Selection icon="IconAirplaneDeparture">
            {chips?.map((filterId) => {
              return (
                <SelectedValue
                  key={filterId}
                  removeOption={() => {
                    onRemoveChip(filterId)
                  }}
                  value={filterId}
                >
                  {filterId}
                </SelectedValue>
              )
            })}
          </DropdownMultiselect.Selection>
          {isTablet && (
            <DropdownMultiselect.Popover>
              <DropdownMultiselect.Options>
                {mappedOptions.map((val) => {
                  return <OptionGroup key={val.id} id={val.id} value={val.value} options={val.children || []} />
                })}
              </DropdownMultiselect.Options>
              <DropdownMultiselect.ConfirmButton>
                {intl.formatMessage({ id: 'quicksearch_save_button_text' })}
              </DropdownMultiselect.ConfirmButton>
            </DropdownMultiselect.Popover>
          )}
          {!isTablet && (
            <DropdownMultiselect.Dialog>
              <DialogPrimitive.Root>
                <DialogPrimitive.Header>
                  <DialogPrimitive.HeaderTitle>
                    <Typography $variant="body-lg-bold">
                      {intl.formatMessage({ id: 'prices_filters_departureairport_label' })}
                    </Typography>
                    <DropdownMultiselect.CrossButton />
                  </DialogPrimitive.HeaderTitle>
                </DialogPrimitive.Header>
                <DialogPrimitive.Body>
                  <DropdownMultiselect.Options>
                    {mappedOptions.map((val) => {
                      return <OptionGroup key={val.id} id={val.id} value={val.value} options={val.children || []} />
                    })}
                  </DropdownMultiselect.Options>
                </DialogPrimitive.Body>
                <DialogPrimitive.Footer>
                  <PricesFilter.Footer>
                    <Button $fullSize onClick={toggle} disabled={disabled}>
                      {disabled && <ButtonLoaderIcon />}
                      {intl.formatMessage({ id: 'quicksearch_save_button_text' })}
                    </Button>
                  </PricesFilter.Footer>
                </DialogPrimitive.Footer>
              </DialogPrimitive.Root>
            </DropdownMultiselect.Dialog>
          )}
        </DropdownMultiselect.Root>
      </FormFieldControl>
    </FormField>
  )
}
